import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import {
  FLAG_GETTING_SERVICE_TYPES,
  GET_SERVICE_TYPES,
  SHOW_ERROR_GETTING_SERVICE_TYPES,
  CLEAR_ERROR_GETTING_SERVICE_TYPES,
  CLEAR_SERVICE_TYPES,
  FLAG_GETTING_SERVICE_TYPE,
  GET_SERVICE_TYPE,
  CLEAR_SERVICE_TYPE,
} from '../types';
import { SERVICE_TYPE_ENDPOINT } from '../../config/endpoints';
import {
  handleResponseError,
  isErrorResponse,
} from '../../utils/error-handlers';
import { DEFAULT_QUERY } from '../../config/queries';
import { DEFAULT_DELETE_CONFIG, DEFAULT_GET_CONFIG, DEFAULT_POST_CONFIG, DEFAULT_PUT_CONFIG } from '../../config/rest';
import { SERVICE_TYPE_PATH } from '../../config/paths';
import { push } from 'react-router-redux';

const flagGettingServiceTypes = (flag) => (dispatch) =>
  dispatch({
    payload: flag,
    type: FLAG_GETTING_SERVICE_TYPES,
  });

const showErrorGettingServiceTypes = (error) => (dispatch) =>
  dispatch({
    payload: error,
    type: SHOW_ERROR_GETTING_SERVICE_TYPES,
  });

const clearErrorGettingServiceTypes = () => (dispatch) =>
  dispatch({
    type: CLEAR_ERROR_GETTING_SERVICE_TYPES,
  });

const getServiceTypes =
  async (name = null, { showOnlyIsCommercial = null }) =>
    async (dispatch) => {
      dispatch(clearErrorGettingServiceTypes());
      dispatch(flagGettingServiceTypes(true));
      let serviceTypes = [];

      const queryFilters = [];

      if (name) queryFilters.push(`name:${name}`);
      if (showOnlyIsCommercial) queryFilters.push(`isCommercial:${showOnlyIsCommercial}`);

      const query = {
        ...DEFAULT_QUERY,
        query: queryFilters,
      };

      const url = `${SERVICE_TYPE_ENDPOINT}?${QueryString.stringify(query)}`;
      try {
        const response = await fetch(url, {
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
        });

        // handle an error response from the server
        const serverError = await handleResponseError(response);
        if (serverError) {
          dispatch(flagGettingServiceTypes(false));
          return dispatch(showErrorGettingServiceTypes(serverError));
        }

        // the response is a response instance
        // parse the data into a usable format using `.json()`
        serviceTypes = await response.json();

        dispatch(flagGettingServiceTypes(false));
        return dispatch({
          type: GET_SERVICE_TYPES,
          payload: serviceTypes,
        });
      } catch (err) {
        dispatch(flagGettingServiceTypes(false));
        return console.error(err);
      }
    };

const clearServiceTypes = () => (dispatch) =>
  dispatch({ type: CLEAR_SERVICE_TYPES });

const getAllServiceTypes = async ({ showOnlyIsCommercial = false } = {}) => async (dispatch) => {
  try {
    dispatch(clearServiceTypes());
    dispatch(flagGettingServiceTypes(true));
    let aditionalParams = '';
    if (showOnlyIsCommercial) {
      aditionalParams = `?isCommercial=${showOnlyIsCommercial}`;
    }
    const response = await fetch(`${SERVICE_TYPE_ENDPOINT}${aditionalParams}`, DEFAULT_GET_CONFIG);
    await isErrorResponse(response);
    const serviceTypes = await response.json();
    dispatch({
      type: GET_SERVICE_TYPES,
      payload: serviceTypes,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagGettingServiceTypes(false));
  }
};

const getFilterServiceTypes = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(clearServiceTypes());
    dispatch(flagGettingServiceTypes(true));
    const query = tableFilters;
    const response = await fetch(`${SERVICE_TYPE_ENDPOINT}?${QueryString.stringify(query)}`, DEFAULT_GET_CONFIG);
    await isErrorResponse(response);
    const serviceTypes = await response.json();
    dispatch({
      type: GET_SERVICE_TYPES,
      payload: serviceTypes,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagGettingServiceTypes(false));
  }
};

const flagGettingServiceType = (flag) => (dispatch) =>
  dispatch({
    payload: flag,
    type: FLAG_GETTING_SERVICE_TYPE,
  });

const getServiceType = async ({ serviceTypeId }) => async (dispatch) => {
  try {
    dispatch(flagGettingServiceType(true));
    const url = `${SERVICE_TYPE_ENDPOINT}/${serviceTypeId}`;
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise);
    const response = await promise.json();
    dispatch({
      type: GET_SERVICE_TYPE,
      payload: response,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagGettingServiceType(false));
  }
}

const createNewServiceType = async ({
  name,
  description
}) => {
  const payload = {
    name,
    description
  };
  const url = SERVICE_TYPE_ENDPOINT;
  const promise = await fetch(url, {
    ...DEFAULT_POST_CONFIG,
    body: JSON.stringify(payload),
  });
  await isErrorResponse(promise);
  const serviceType = await promise.json();
  return serviceType;
};

const postServiceType =
  async ({
    name,
    description
  }) =>
    async (dispatch) => {
      try {
        dispatch(flagGettingServiceType(true));
        const response = await createNewServiceType({
          name,
          description
        });
        if (response) {
          dispatch(push(`${SERVICE_TYPE_PATH}/${response.id}`));
        }
      } catch ({ message }) {
        toastr.error('Error', message);
      } finally {
        dispatch(flagGettingServiceType(false));
      }
    };

const putServiceType =
  async (
    serviceTypeId,
    {
      name,
      description
    },
  ) =>
    async (dispatch) => {
      try {
        dispatch(flagGettingServiceType(true));
        const payload = {
          id: serviceTypeId,
          name,
      description
        };
        const url = `${SERVICE_TYPE_ENDPOINT}/${serviceTypeId}`;
        const promise = await fetch(url, {
          ...DEFAULT_PUT_CONFIG,
          body: JSON.stringify(payload),
        });
        await isErrorResponse(promise, null, dispatch);
        await promise.json();
        dispatch(push(`${SERVICE_TYPE_PATH}/${serviceTypeId}`));
      } catch ({ message }) {
        toastr.error('Error', message);
      } finally {
        dispatch(flagGettingServiceType(false));
      }
    };

const deleteServiceType =
  async ({ serviceTypeId }) =>
    async (dispatch) => {
      try {
        dispatch(flagGettingServiceType(true));
        const url = `${SERVICE_TYPE_ENDPOINT}/${serviceTypeId}`;
        const promise = await fetch(url, {
          ...DEFAULT_DELETE_CONFIG,
        });
        await isErrorResponse(promise, null, dispatch);
        await promise.json();
        dispatch(push(SERVICE_TYPE_PATH));
      } catch ({ message }) {
        toastr.error('Error', message);
      } finally {
        dispatch(flagGettingServiceType(false));
      }
    };

const clearServiceType = () => (dispatch) =>
  dispatch({ type: CLEAR_SERVICE_TYPE });

export { getFilterServiceTypes, getServiceTypes, clearErrorGettingServiceTypes, getAllServiceTypes, clearServiceTypes, getServiceType, clearServiceType, postServiceType, putServiceType, deleteServiceType };
